// Ambulance-style siren for new-order alerts, generated with the Web Audio API (no sound file).
// It repeats until it is switched off, so a busy vendor can't miss an order.
//
// Browsers only allow sound after the user has interacted with the page, so the audio context is
// created on the first click / key press / touch. If an alert is already waiting when that first
// interaction happens, the siren starts right then.

const HIGH_HZ = 960;
const LOW_HZ = 770;
const STEP_SECONDS = 0.65; // how long each of the two tones lasts
const SCHEDULE_AHEAD_SECONDS = 120; // tones are queued on the audio clock, so tab throttling can't stall them
const RESCHEDULE_EVERY_MS = 30000;

interface Siren {
  oscillator: OscillatorNode;
  gain: GainNode;
  timer: ReturnType<typeof setInterval>;
  nextAt: number;
  high: boolean;
}

let context: AudioContext | null = null;
let listening = false;
let wanted = false;
let siren: Siren | null = null;

const scheduleTones = () => {
  if (!context || !siren) return;
  const current = siren;
  let time = Math.max(current.nextAt, context.currentTime);
  const end = time + SCHEDULE_AHEAD_SECONDS;
  while (time < end) {
    current.oscillator.frequency.setValueAtTime(current.high ? HIGH_HZ : LOW_HZ, time);
    current.high = !current.high;
    time += STEP_SECONDS;
  }
  current.nextAt = time;
};

const startSiren = () => {
  if (siren || !context || context.state !== "running") return;
  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "triangle";
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.35, now + 0.05);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  siren = { oscillator, gain, timer: setInterval(scheduleTones, RESCHEDULE_EVERY_MS), nextAt: now, high: true };
  scheduleTones();
};

const stopSiren = () => {
  const current = siren;
  siren = null;
  if (!current || !context) return;
  clearInterval(current.timer);
  const now = context.currentTime;
  current.gain.gain.cancelScheduledValues(now);
  current.gain.gain.setValueAtTime(Math.max(current.gain.gain.value, 0.0001), now);
  current.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
  current.oscillator.stop(now + 0.15);
};

const syncSiren = () => {
  if (wanted) startSiren();
  else stopSiren();
};

const unlock = () => {
  try {
    if (!context) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return;
      context = new Ctor();
    }
    if (context.state === "suspended") {
      context.resume().then(syncSiren).catch(() => undefined);
    } else {
      syncSiren();
    }
  } catch {
    // audio unavailable: the on-screen alert still works
  }
};

// Call once; sound is enabled by the first interaction with the page
export const enableNotificationSoundOnInteraction = () => {
  if (listening) return;
  listening = true;
  ["pointerdown", "keydown", "touchstart"].forEach((name) =>
    window.addEventListener(name, unlock, { passive: true })
  );
};

// Turns the siren on while an alert is waiting and off once none is left
export const setAlertSoundActive = (active: boolean) => {
  wanted = active;
  syncSiren();
};
